import React, { useState, useRef, useEffect } from "react";
import { Box, Typography, TextField, Divider } from "@mui/material";
import { Question } from "./type";
import { RenderHTMLContent } from "./extractQuestions";

// Props for the SAQuestion component
interface SAQuestionProps {
  question: Question;
  editable?: boolean;
  onAnswerChange?: (answer: string) => void;
  answer?: string;
  formatSize?: number;
  checkCorrect?: boolean;
}

const SAQuestion: React.FC<SAQuestionProps> = ({
  question,
  editable,
  onAnswerChange,
  answer,
  formatSize = 1,
  checkCorrect = false,
}) => {
  const [answers, setAnswers] = useState({
    A: "",
    B: "",
    C: "",
    D: "",
  });

  // Create refs for the input fields
  const inputRefs = {
    A: useRef<HTMLInputElement>(null),
    B: useRef<HTMLInputElement>(null),
    C: useRef<HTMLInputElement>(null),
    D: useRef<HTMLInputElement>(null),
  };

  // Set the correct answers for the question
  useEffect(() => {
    let str = answer !== undefined ? answer : question.correctAnswer;
    if (!str) {
      str = "";
    }
    // Split the answer string
    const correctAnswers = str.split("");
    if (correctAnswers[0] === " ") {
      correctAnswers.shift();
    }
    // If the correct answer length is less than 4, pad it with empty strings to make it 4
    while (correctAnswers.length < 4) {
      correctAnswers.push("");
    }
    setAnswers({
      A: correctAnswers[0],
      B: correctAnswers[1],
      C: correctAnswers[2],
      D: correctAnswers[3],
    });
  }, [question.correctAnswer, editable, answer]);

  const handleInputChange = (part: "A" | "B" | "C" | "D", value: string) => {
    if (checkCorrect) return;
    // Only accept '-', ',' or numbers
    const regex = /^[-,0-9]*$/;
    if (regex.test(value)) {
      const newAnswers = { ...answers, [part]: value };
      setAnswers(newAnswers);

      onAnswerChange &&
        onAnswerChange(
          `${newAnswers.A || ""}${newAnswers.B || ""}${newAnswers.C || ""}${
            newAnswers.D || ""
          }`,
        ); // Convert the answers to a string

      // Automatically move to the next input field if the value is not empty
      if (value && part !== "D") {
        const nextPart = String.fromCharCode(part.charCodeAt(0) + 1) as
          | "A"
          | "B"
          | "C"
          | "D";
        inputRefs[nextPart]?.current?.focus();
      }
    }
  };

  const handleKeyDown = (
    part: "A" | "B" | "C" | "D",
    event: React.KeyboardEvent,
  ) => {
    if (event.key === "Backspace" && !answers[part]) {
      const prevPart = String.fromCharCode(part.charCodeAt(0) - 1) as
        | "A"
        | "B"
        | "C"
        | "D";

      inputRefs[prevPart]?.current?.focus();
    }
  };

  // // Function to check if the user's answers are correct
  // const isAnswerCorrect = (part: 'A' | 'B' | 'C' | 'D') => {
  //   const answerValue = answers[part];
  //   const index = part.charCodeAt(0) - 'A'.charCodeAt(0); // Get the index for A, B, C, D
  //   return answerValue === correctAnswers[index];
  // };

  const isGreen =
    checkCorrect &&
    Object.keys(answers).every((key) => {
      const k = key as "A" | "B" | "C" | "D";
      return answers[k] === question.correctAnswer[k.charCodeAt(0) - 65];
    });
  const isRed = checkCorrect && !isGreen;

  const textColor = isGreen
    ? "answer.correct"
    : isRed
      ? "answer.incorrect"
      : "text.primary";
  return (
    <Box display="flex" mt={0.5} mb={1} gap={1}>
      {(["A", "B", "C", "D"] as const).map((part) => (
        <TextField
          key={part}
          variant="standard"
          autoComplete="off"
          inputMode="numeric"
          value={answers[part]}
          inputRef={inputRefs[part]}
          disabled={!editable || checkCorrect}
          onChange={(e) => handleInputChange(part, e.target.value)}
          onKeyDown={(e) => handleKeyDown(part, e)}
          inputProps={{
            maxLength: 1,
            style: {
              textAlign: "center",
              fontSize: `${formatSize - 0.1}rem`,
              fontWeight: 500,
              padding: 0,
            },
          }}
          sx={{
            width: 25, // 👈 rất nhỏ
            minWidth: 25,

            "& .MuiInput-root": {
              "&:before": {
                borderBottomColor: "primary.light",
              },
              "&:hover:not(.Mui-disabled):before": {
                borderBottomColor: "text.primary",
              },
              "&:after": {
                borderBottomColor: "text.primary",
              },
              "&.Mui-disabled input": {
                color: textColor,
                WebkitTextFillColor: "currentColor", // 👈 bắt buộc cho Chrome
              },
            },
          }}
        />
      ))}
    </Box>
  );
};

export default SAQuestion;
